import { Button } from '@kerfjs/ui/button';
import { List } from '@kerfjs/ui/list';
import { ListHeader } from '@kerfjs/ui/list-header';
import { ListInsetControl } from '@kerfjs/ui/list-inset-control';
import { LucideIcon } from '@kerfjs/ui/lucide-icon';
import { Text } from '@kerfjs/ui/text';
import type { SafeHtml } from 'kerfjs';
import { Plus } from 'lucide';

import type { DemandListItem } from '../../../data/api.js';
import {
  cancelledExpanded,
  demandListLoadedKey,
  demands,
  demandsLoadingMore,
  demandsNextCursor,
  demandStatusCounts,
  draftsExpanded,
  me,
  shellSearch,
  shellSearchTokens,
} from '../../../data/state.js';
import { currentDemandId } from '../../../lib/context.js';
import { demandListFilterQuery } from '../../../lib/demand-list-query.js';
import { filterDemandsForShellSearch } from '../../../lib/shell-search.js';
import { DemandSidebarRow } from './demand-sidebar-row.js';

const DEMAND_GROUPS: { status: string; label: string }[] = [
  { status: 'submitted', label: 'Pending Review' },
  { status: 'returned', label: 'Needs Input' },
  { status: 'quoting', label: 'Getting Quotes' },
  { status: 'analysed', label: 'Ready to Decide' },
  { status: 'decided', label: 'Decided' },
  { status: 'closed', label: 'Complete' },
  // Every line rejected, kept apart from purchases (HS2-Z6MFSA).
  { status: 'rejected', label: 'Rejected' },
];

/**
 * Whether the shown rows answer the current search, so the server's per-status
 * totals describe them (HS2-X9K02S). While a new search is still on its way the
 * client filter narrows the loaded rows, and the groups count those instead.
 */
function serverCountsApply(): boolean {
  const filter = demandListFilterQuery({
    status: '',
    search: shellSearch.value,
    tokens: shellSearchTokens.value,
    searchActive: true,
  });
  return demandListLoadedKey.value === `${me.value?.organizationId ?? ''}?${filter}`;
}

/** "Load more" below the groups while the list has further pages (HS2-X9K02S). */
function DemandsLoadMore({ remaining }: { remaining: number }): SafeHtml {
  if (demandsNextCursor.value === null) return <></>;
  const loading = demandsLoadingMore.value;
  return (
    <ListInsetControl>
      <Button action="demands-load-more" tone="neutral" emphasis="secondary" size="compact" loading={loading}>
        {remaining > 0 ? `Load more (${String(remaining)} more)` : 'Load more'}
      </Button>
    </ListInsetControl>
  );
}

/** Demand navigation grouped by lifecycle status, one page at a time (HS2-X9K02S). */
export function DemandsSidebar(): SafeHtml {
  const all = filterDemandsForShellSearch(
    demands.value,
    shellSearch.value,
    shellSearchTokens.value,
    me.value?.memberId,
  );
  const counts = serverCountsApply() ? demandStatusCounts.value : undefined;
  const total = (status: string, shown: number): number => Math.max(counts?.[status] ?? 0, shown);
  const remaining = counts === undefined ? 0 : Object.values(counts).reduce((sum, n) => sum + n, 0) - all.length;
  const activeId = currentDemandId();
  const byStatus = new Map<string, DemandListItem[]>();
  for (const demand of all) {
    const group = byStatus.get(demand.status) ?? [];
    group.push(demand);
    byStatus.set(demand.status, group);
  }
  const drafts = byStatus.get('draft') ?? [];
  const cancelled = byStatus.get('cancelled') ?? [];
  return (
    <List className="demands-sidebar" gap="m">
      <ListInsetControl>
        <Button
          action="new-demand"
          tone="brand"
          emphasis="primary"
          size="default"
          icon={<LucideIcon icon={Plus} name="" />}
        >
          New Demand
        </Button>
      </ListInsetControl>
      {all.length === 0 ? (
        <Text tone="quiet" size="compact">
          No demands yet.
        </Text>
      ) : null}
      {DEMAND_GROUPS.map((group) => {
        const rows = byStatus.get(group.status) ?? [];
        return rows.length === 0 ? null : (
          <List className="demand-group" rootAttributes={{ 'data-key': group.status }}>
            <ListHeader
              label={group.label}
              count={total(group.status, rows.length)}
              countLabel={`${String(total(group.status, rows.length))} demands`}
            />
            {rows.map((demand) => (
              <DemandSidebarRow demand={demand} activeId={activeId} />
            ))}
          </List>
        );
      })}
      {drafts.length > 0 ? (
        <List className="demand-group" rootAttributes={{ 'data-key': 'draft' }}>
          <ListHeader
            label="Drafts"
            count={total('draft', drafts.length)}
            countLabel={`${String(total('draft', drafts.length))} drafts`}
            toggle={true}
            expanded={draftsExpanded.value}
            action="toggle-drafts"
          />
          {draftsExpanded.value
            ? drafts.map((demand) => <DemandSidebarRow demand={demand} activeId={activeId} />)
            : null}
        </List>
      ) : null}
      {cancelled.length > 0 ? (
        <List className="demand-group" rootAttributes={{ 'data-key': 'cancelled' }}>
          <ListHeader
            label="Cancelled"
            count={total('cancelled', cancelled.length)}
            countLabel={`${String(total('cancelled', cancelled.length))} cancelled`}
            toggle={true}
            expanded={cancelledExpanded.value}
            action="toggle-cancelled"
          />
          {cancelledExpanded.value
            ? cancelled.map((demand) => <DemandSidebarRow demand={demand} activeId={activeId} />)
            : null}
        </List>
      ) : null}
      <DemandsLoadMore remaining={remaining} />
    </List>
  );
}
